<template>
	<v-container grid-list-md>
		<v-row>
			<v-col>
				<v-form ref="form">
					<fieldset :disabled="salvando">
						<v-card>
							<v-card-title>
								{{ novo ? 'Registrar' : 'Editar' }} Loja
							</v-card-title>
							<v-card-text>
								<v-container
									fluid
									class="pa-0"
								>
									<AppLoading v-if="carregando" />
									<div v-else-if="erro"> {{ erro || 'Não encontrado' }} </div>
									<template v-else>
										<v-row>
											<v-col cols="12" class="text-center d-flex flex-column align-center">
												<v-avatar size="90">
													<img
														v-if="logoLoja"
														:src="logoLoja"
													/>
													<v-icon
														v-else
														v-text="'mdi-bank'"
														size="90"
													/>
												</v-avatar>
												<v-menu offset-y>
													<template v-slot:activator="{ on }">
														<v-btn
															outlined
															color="primary"
															v-on="on"
														>
															Alterar Logo
														</v-btn>
													</template>
													<v-list>
														<v-list-item @click="() => logoInput.click()">
															Enviar Novo
														</v-list-item>
														<v-list-item v-if="logoLoja" @click="removerLogo">
															Remover
														</v-list-item>
													</v-list>
												</v-menu>
												<input
													type="file"
													accept="image/*"
													ref="logoInput"
													style="display: none;"
													@change="onLogoChange"
												>
											</v-col>
											<v-col cols="6">
												<v-text-field
													label="Nome"
													v-model="loja.nome"
													:rules="[ obrigatorio ]"
													required
												/>
											</v-col>
											<v-col cols="6">
												<SeletorDePotencia
													label="Potência"
													v-model="loja.potencia"
													:rules="[ obrigatorio ]"
													required
													:disabled="!intersectRoles(authModule.role, 'admin')"
													:return-object="false"
												/>
											</v-col>
											<v-col cols="12" sm="6">
												<v-text-field
													label="Período das Sessões"
													v-model="loja.periodoDaSessao"
													hint="Ex: Terças-feiras, 1ª Quarta-feira do Mês, 1º e 3º Sábado do Mês..."
												/>
											</v-col>
											<v-col cols="12" sm="6">
												<v-text-field
													type="time"
													step="1800"
													min="00:00"
													max="23:30"
													label="Horário da Sessão"
													v-model="loja.horarioDaSessao"
												/>
											</v-col>
											<v-col cols="12">
												<v-text-field
													label="Endereço"
													v-model="loja.endereco"
												/>
											</v-col>
											<v-col cols="12">
												<v-text-field
													label="Abreviação"
													v-model="loja.abreviacao"
													maxlength="5"
													hint="Aparece no calendário em dispositivos pequenos (5 letras)"
												/>
											</v-col>
											<v-col cols="12">
												<v-text-field
													label="Templo"
													v-model="loja.templo"
												/>
											</v-col>
										</v-row>
										<div>
											Comprometemos a Manter Atualizado
										</div>
										<v-row>
											<v-col cols="12" md="auto">
												<v-checkbox
													label="A Agenda de Sessões"
													v-model="loja.compromissosDoApp.agendaDeSessoes"
													hide-details
													single-line
												/>
											</v-col>
											<v-col cols="12" md="auto">
												<v-checkbox
													label="Os Dados dos Irmãos"
													v-model="loja.compromissosDoApp.dadosDosIrmaos"
													hide-details
													single-line
												/>
											</v-col>
											<v-col cols="12" md="auto">
												<v-checkbox
													label="Os Candidatos da Loja"
													v-model="loja.compromissosDoApp.candidatosDaLoja"
													hide-details
													single-line
												/>
											</v-col>
										</v-row>
									</template>
								</v-container>
							</v-card-text>
							<v-card-actions>
								<v-btn
									color="grey"
									text
									@click="() => $router.go(-1)"
								> Cancelar </v-btn>
								<v-spacer></v-spacer>
								<v-btn
									color="primary"
									@click="salvar"
									:loading="salvando"
								> Salvar Loja </v-btn>
							</v-card-actions>
						</v-card>
					</fieldset>
				</v-form>
			</v-col>
		</v-row>
	</v-container>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { InputLoja } from '@/../common/types/Loja'
import { obrigatorio } from '@/shareds/regras'
import { criarLoja, storageLogoLojaRef } from '@/shareds/sharedsLoja'
import LojaService from '@/services/LojaService'
import SeletorDePotencia from '@/components/SeletorDePotencia.vue'
import { authModule, alert } from '@/store'
import { storage } from '@/firebase'
import { v4 as uuidv4 } from 'uuid'
import { intersectRoles } from '../../helpers/auth-helpers'
import { onBeforeRouteLeave, useRouter } from '@/composables/router'

const props = defineProps({
	novo: { type: Boolean, default: false },
	id: { type: String },
})

const router = useRouter()

const versaoAnterior = ref<string | null>(null)
const carregando = ref(true)
const erro = ref<string | null>(null)
const loja = ref<InputLoja>(criarLoja())

const salvando = ref(false)

const form = ref<HTMLFormElement & {
	validate: (() => boolean),
}>()
const logoInput = ref<HTMLInputElement>()

const novaLogo = ref<File | null>(null)
const logoPreview = ref('')

const logoLoja = computed(() => logoPreview.value || loja.value.urlLogo)

function removerLogo() {
	loja.value.urlLogo = ''
	novaLogo.value = null
}

function onLogoChange() {
	const input = logoInput.value!
	if (!input.files) return
	const [file] = input.files
	novaLogo.value = file
}

watch(novaLogo, (logo) => {
	if (!logo) {
		logoPreview.value = ''
		return
	}
	const reader = new FileReader()

	reader.addEventListener('load', () => {
		logoPreview.value = reader.result as string
	}, false)

	reader.readAsDataURL(logo)
}, { immediate: true })

async function carregarLoja() {
	carregando.value = true
	erro.value = null
	try {
		loja.value = props.novo
			? criarLoja()
			: await (LojaService
				.get(props.id!)
				.then((loja) => ({
					...loja,
					potencia: loja.potencia._id,
					somar: (loja.somar && typeof loja.somar === 'object') ? loja.somar?._id : loja.somar,
				})))
		nextTick().then(() => {
			versaoAnterior.value = JSON.stringify(loja.value)
		})
	} catch (error) {
		erro.value = (error as any).message
	} finally {
		nextTick(() => {
			carregando.value = false
		})
	}
}

carregarLoja()

async function salvar() {
	if (!form.value!.validate()) { return }
	salvando.value = true
	try {
		if (novaLogo.value) {
			const ref = storage.ref(`${storageLogoLojaRef}/${uuidv4()}`)
			const snapshot = await ref.put(novaLogo.value, {
				customMetadata: {
					fileName: novaLogo.value.name,
				},
			})
			const donwloadUrl = await snapshot.ref.getDownloadURL()
			loja.value.urlLogo = donwloadUrl || ''
		}
		props.novo
			? await LojaService.post(loja.value)
			: await LojaService.put(props.id!, loja.value)
		versaoAnterior.value = JSON.stringify(loja.value)
		router.go(-1)
	} catch (error) {
		alert.setError(error)
	} finally {
		salvando.value = false
	}
}

function validarSaida(): false | void {
	if(versaoAnterior.value === JSON.stringify(loja.value)) return
	return confirm("Alguns dados não foram salvos. Deseja mesmo sair?") && undefined
}

onBeforeRouteLeave((to, from, next) => {
	next(validarSaida())
})
</script>
